import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Users, Building2, Wallet, TrendingUp, Coins, ShieldCheck,
  FileCheck2, UserCheck, AlertTriangle, Link2, Award, BookText,
  Search,
} from "lucide-react";

const ENTITIES = [
  {
    id: "borrower", icon: Users, color: "#0055FF", title: "Borrower (Debitur)",
    subtitle: "Entitas penerima fasilitas kredit",
    why: "Satu borrower bisa punya banyak facility. Risk profile (rating, sektor, group) menentukan harga & limit cap.",
    fields: [
      ["borrower_id", "Kode internal (BRW-0001)"],
      ["cif", "Customer Information File — global key"],
      ["borrower_type", "Corporate / SME / Commercial / Individual"],
      ["sector", "Industry classification untuk concentration limit"],
      ["risk_rating", "AAA…D — input ke perhitungan PD"],
      ["group_id", "Group obligor parent (konglomerasi)"],
      ["shareholder_ids", "Untuk deteksi related party"],
      ["address, director", "Connected obligor detection"],
    ],
    behavior: [
      "DELETE tidak cascade — facility milik borrower tetap ada",
      "Bisa belong ke 1 group obligor",
      "Bisa punya multiple shareholders",
    ],
  },
  {
    id: "group", icon: Building2, color: "#1f1f1f", title: "Group Obligor (Konglomerasi)",
    subtitle: "Kumpulan borrower yang dianggap satu obligor untuk limit",
    why: "Regulasi BMPK (PBI/POJK) tidak hanya per borrower tapi per group. 1 group tidak boleh > 25% modal bank. Astra Group, Sinar Mas Group adalah contoh.",
    fields: [
      ["group_id", "GRP-001"],
      ["group_name", "Grup Astra Konglomerat"],
      ["group_limit", "Plafond konsolidasi untuk semua anggota"],
      ["total_exposure", "AUTO — Σ outstanding semua anggota"],
      ["available_limit", "AUTO — group_limit - total_exposure"],
      ["breach", "AUTO — flag jika total_exposure > group_limit"],
    ],
    behavior: [
      "Server auto-aggregasi total_approved, utilized, exposure",
      "Alert GROUP_LIMIT_BREACH (critical) jika breach=true",
    ],
  },
  {
    id: "facility", icon: Wallet, color: "#0055FF", title: "Facility (Fasilitas Kredit)",
    subtitle: "Plafond kredit di-approve untuk borrower — produk inti bank",
    why: "Object utama yang dikelola sistem. Setiap facility punya limit, utilisasi, jatuh tempo, dan dapat di-secure dengan collateral/guarantor.",
    fields: [
      ["facility_type", "Working Capital, Term Loan, Trade Finance, Overdraft, Bank Guarantee"],
      ["approved_limit", "Plafond — hard cap (kecuali excess acknowledged)"],
      ["utilized_limit", "Outstanding terpakai"],
      ["blocked_amount", "Earmark untuk LC/BG belum di-claim"],
      ["available_limit", "AUTO — max(0, approved - utilized - blocked)"],
      ["allow_excess + excess_reason", "Audit acknowledge untuk excess sah"],
      ["start_date, maturity_date", "Periode fasilitas"],
    ],
    behavior: [
      "Server REJECT 422 jika utilized + blocked > approved (kecuali allow_excess + reason)",
      "Alert HIGH_UTILIZATION jika ratio ≥ 90%",
      "Alert MATURITY_SOON jika ≤ 30 hari lagi",
      "Utilization ratio = utilized / approved",
    ],
  },
  {
    id: "exposure", icon: TrendingUp, color: "#7c3aed", title: "Exposure (Eksposur Risk)",
    subtitle: "Snapshot risk-based metrics per facility per tanggal",
    why: "Beda dengan 'utilized' (billing): exposure fokus ke potential loss. Di-report bulanan ke regulator. Multiple records per facility = historical timeline.",
    fields: [
      ["outstanding_amount", "Outstanding aktual saat reporting_date"],
      ["undrawn_commitment", "Bagian limit belum ditarik"],
      ["ead", "Exposure At Default = outstanding + α × undrawn"],
      ["pd", "Probability of Default (0..1)"],
      ["lgd", "Loss Given Default (0..1) — setelah collateral"],
      ["expected_loss", "AUTO — EAD × PD × LGD"],
      ["reporting_date", "Tanggal cut-off"],
    ],
    behavior: [
      "expected_loss tidak diterima dari client — selalu re-compute server",
      "Pydantic constraint: 0 ≤ pd ≤ 1, 0 ≤ lgd ≤ 1",
    ],
  },
  {
    id: "collateral", icon: Coins, color: "#008A00", title: "Collateral (Jaminan)",
    subtitle: "Aset yang dijaminkan — secondary repayment source",
    why: "Mengurangi LGD → mengurangi Expected Loss → mengurangi RWA per Basel III. Wajib untuk produk tertentu. Bisa cross-collateralization (1 collateral cover banyak facility).",
    fields: [
      ["collateral_type", "Tanah & Bangunan, Deposito, Kendaraan, Inventory, Mesin"],
      ["market_value", "Nilai pasar dari appraiser independen"],
      ["haircut", "Diskon untuk uncertainty likuidasi (0..1)"],
      ["lendable_value", "AUTO — market × (1 - haircut)"],
      ["valuation_date", "Terakhir di-appraise"],
      ["expiry_date", "Untuk asset yang ada masa berlaku"],
      ["facility_ids[]", "1 collateral bisa secure banyak facility"],
    ],
    behavior: [
      "Alert COLLATERAL_SHORTFALL jika lendable < Σ outstanding attached facilities",
      "Alert EXPIRED_COLLATERAL jika expiry_date < now",
      "1 collateral attach ke 2+ borrower → connected obligor via COLLATERAL",
    ],
  },
  {
    id: "guarantor", icon: ShieldCheck, color: "#F5A623", title: "Guarantor (Penjamin)",
    subtitle: "Pihak ketiga yang menjamin pembayaran",
    why: "Sumber pelunasan tambahan saat borrower default. 1 guarantor bisa menjamin banyak facility — saat default, claim ke guarantor sampai guarantee_amount.",
    fields: [
      ["guarantor_type", "Personal / Corporate"],
      ["guarantee_amount", "Plafond maksimum yang dijamin"],
      ["expiry_date", "Masa berlaku garansi"],
      ["facility_ids[]", "Facility yang dijamin"],
    ],
    behavior: [
      "Alert EXPIRED_GUARANTEE jika expiry < now",
      "1 guarantor cover 2+ borrower → connected obligor via GUARANTOR",
    ],
  },
  {
    id: "covenant", icon: FileCheck2, color: "#777777", title: "Covenant (Klausul Perjanjian)",
    subtitle: "Janji finansial/operasional borrower selama facility aktif",
    why: "Pelanggaran = event of default. Bank monitor quarterly. Tipe umum: DSCR Min, DER Max, Current Ratio, Interest Coverage, EBITDA Margin.",
    fields: [
      ["covenant_type", "DSCR Minimum / DER Maximum / Current Ratio / dll."],
      ["threshold_value", "Batas yang dijaga"],
      ["actual_value", "Hasil pengukuran terakhir"],
      ["measurement_date", "Tanggal hitung (quarterly)"],
      ["status", "Compliant / Breach"],
    ],
    behavior: [
      "Alert COVENANT_BREACH (critical) jika status=Breach",
      "Saat breach, opsi bank: waiver / restructure / call default",
    ],
  },
  {
    id: "approval", icon: UserCheck, color: "#4f46e5", title: "Approval (Workflow Persetujuan)",
    subtitle: "Permintaan perubahan facility yang butuh higher-level approval",
    why: "4-eyes principle. Credit Officer submit, Approver decide. Bukan bypass via direct PUT — agar ada audit trail dan separation of duty.",
    fields: [
      ["request_type", "new_facility / limit_increase / limit_decrease / limit_renewal / collateral_change"],
      ["requested_by, requested_by_name", "Credit Officer"],
      ["approved_by, approved_by_name", "Approver"],
      ["approval_status", "Pending → Approved/Rejected/Revision"],
      ["payload", "Contoh: {new_limit: 600000000000}"],
      ["notes", "Catatan keputusan"],
    ],
    behavior: [
      "Saat Approved + limit_increase/decrease/renewal → server auto-apply ke facility",
      "Saat limit_decrease yang akan bikin over-limit → REJECT 422 kecuali facility allow_excess",
      "Semua decision tercatat di audit_logs",
    ],
  },
  {
    id: "alert", icon: AlertTriangle, color: "#D0021B", title: "Alert (Early Warning System)",
    subtitle: "Notifikasi otomatis dari rule engine — 8 tipe",
    why: "Risk Officer perlu signal sebelum jadi crisis. Engine recompute alerts setiap mutation pada facility/exposure/collateral/covenant/guarantor.",
    fields: [
      ["alert_type", "LIMIT_BREACH, HIGH_UTILIZATION, GROUP_LIMIT_BREACH, COLLATERAL_SHORTFALL, COVENANT_BREACH, MATURITY_SOON, EXPIRED_COLLATERAL, EXPIRED_GUARANTEE, EXCESS_ACKNOWLEDGED"],
      ["severity", "critical / warning / info"],
      ["status", "Open / Resolved"],
      ["related_entity", "Facility / Borrower / Group / Collateral / Guarantor / Covenant"],
    ],
    behavior: [
      "Idempotent — old alerts cleared, fresh ones generated setiap regenerate",
      "Risk officer bisa manual resolve (status Open → Resolved)",
      "Auto-regenerate setelah setiap CREATE/UPDATE/DELETE",
    ],
  },
  {
    id: "shareholder", icon: Award, color: "#555555", title: "Shareholder (Pemegang Saham)",
    subtitle: "Untuk deteksi related party",
    why: "Per POJK 32/2018 tentang BMPK, related party (via shared shareholder) harus dikonsolidasi exposure-nya. Sistem auto-flag 2 borrower yang share shareholder.",
    fields: [
      ["shareholder_id", "SH-XXXX"],
      ["name", "PT Astra International Holding"],
      ["ownership_type", "Corporate / Individual"],
    ],
    behavior: [
      "Tampil di graph view sebagai abu-abu",
      "2 borrower share shareholder → connected obligor type SHAREHOLDER",
    ],
  },
];

const FORMULAS = [
  { label: "available_limit", formula: "max(0, approved_limit - utilized_limit - blocked_amount)", purpose: "Sisa kapasitas drawdown" },
  { label: "utilization_ratio", formula: "utilized_limit / approved_limit", purpose: "Util % per facility" },
  { label: "lendable_value", formula: "market_value × (1 - haircut)", purpose: "Nilai collateral efektif" },
  { label: "expected_loss", formula: "EAD × PD × LGD", purpose: "Provisioning per Basel III" },
  { label: "group_exposure", formula: "Σ outstanding_amount semua facility anggota", purpose: "BMPK per group" },
];

export default function ProductGuide() {
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("borrower");

  const filtered = ENTITIES.filter((e) =>
    !search ||
    e.title.toLowerCase().includes(search.toLowerCase()) ||
    e.subtitle.toLowerCase().includes(search.toLowerCase()) ||
    e.why.toLowerCase().includes(search.toLowerCase())
  );

  const current = ENTITIES.find((e) => e.id === active);

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="product-guide-page">
      <PageHeader
        title="Panduan Produk & Behavior Sistem"
        subtitle="Pelajari relevancy setiap entitas, field, formula, dan rule engine"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="space-y-3">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cari entitas…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="guide-search"
            />
          </div>
          <div className="space-y-1">
            {filtered.map((e) => {
              const Icon = e.icon;
              return (
                <button
                  key={e.id}
                  onClick={() => setActive(e.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm border transition-colors ${
                    active === e.id
                      ? "border-foreground bg-foreground/5 font-semibold"
                      : "border-border hover:border-foreground/30"
                  }`}
                  data-testid={`guide-tab-${e.id}`}
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded" style={{ background: e.color + "20", color: e.color }}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-left">{e.title.replace(/\s*\(.*\)/, "")}</span>
                </button>
              );
            })}
          </div>

          {/* Formula recap */}
          <div className="border border-border rounded-md bg-card p-4 mt-4">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <BookText className="h-3.5 w-3.5" /> Formula Recap
            </h3>
            <div className="space-y-2.5">
              {FORMULAS.map((f) => (
                <div key={f.label} className="text-xs">
                  <div className="font-semibold">{f.label}</div>
                  <div className="mono text-[10px] text-muted-foreground">{f.formula}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Main content */}
        {current && (
          <main className="space-y-5">
            <div className="border border-border rounded-md bg-card p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 rounded-md" style={{ background: current.color + "20", color: current.color }}>
                  <current.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold tracking-tight">{current.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{current.subtitle}</p>
                </div>
              </div>

              <div className="border-l-2 border-foreground/20 pl-4 py-1 mb-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Mengapa Penting</div>
                <p className="text-sm leading-relaxed">{current.why}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Field Utama</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {current.fields.map(([name, desc]) => (
                        <tr key={name} className="border-b border-border last:border-0">
                          <td className="py-1.5 mono font-semibold pr-3 align-top whitespace-nowrap">{name}</td>
                          <td className="py-1.5 text-muted-foreground">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Behavior Sistem</div>
                  <ul className="text-xs space-y-1.5">
                    {current.behavior.map((b, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-foreground mt-1">→</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="border border-border rounded-md bg-zinc-50 p-4 text-xs text-muted-foreground">
              <Badge variant="outline" className="mb-2">Tip</Badge>
              <div>
                Dokumentasi lengkap dengan diagram relasi & contoh skenario tersedia di
                <span className="mono ml-1">/app/memory/PRODUCT_CATALOG.md</span>.
                Untuk integrasi via API, lihat menu <b>Integrasi API</b>.
              </div>
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
